import type { ReactElement } from "react";
import { ArticleHero } from "../ArticleHero";
import familyHeroImage from "../../assets/images/family-fall-outfit-portrait.webp";
import family5 from "../../assets/images/family-autumn-park-bench-portrait.webp";
import family6 from "../../assets/images/mother-children-winter-holiday-portrait.webp";
import { ArticlePhoto } from "./ArticlePhoto";
import { UiIcon } from "./components/UiIcons";
import { GuideCtaActions } from "./components/GuideCtaActions";
import "./NewbornPages.scss";
import "./NewbornArticle.scss";
import "./FamilyArticle.scss";

const kidPlan = [
  {
    n: "01",
    title: "Keep it simple",
    text: "Describe photo day as time to play together, not a test of good behavior.",
  },
  { n: "02", title: "Protect their rhythm", text: "Plan around sleep and food, with a quiet hour before you leave." },
  {
    n: "03",
    title: "Bring one comfort",
    text: "A familiar snack or small toy can help without taking over the session.",
  },
  {
    n: "04",
    title: "Let connection lead",
    text: "Follow my prompts and enjoy your children instead of asking for smiles.",
  },
];

export const HowToPrepareKidsForFamilyPhotos = (): ReactElement => (
  <main className="newborn-page newborn-article-page family-article-page">
    <article>
      <ArticleHero
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Resources", to: "/resources/" },
          { label: "Family", to: "/resources/family/" },
          { label: "How to Prepare Kids for Family Photos" },
        ]}
        category="Family preparation guide"
        description="A low-pressure plan that helps children arrive comfortable, connected and free to be themselves."
        imageAlt="Children playing naturally with their parents during family photographs"
        imagePosition="center 48%"
        imageSrc={familyHeroImage}
        readingTime="6 min read"
        title="How to Prepare Kids for Family Photos"
        updatedAt="2026-08-04"
        updatedLabel="Updated August 2026"
      />
      <div className="newborn-shell newborn-article-layout">
        <aside className="newborn-article-callout">
          <span className="newborn-article-callout__icon">
            <UiIcon name="star" />
          </span>
          <div>
            <h2>Quick answer</h2>
            <p>
              Tell children they are going to play and spend time together—not that they must smile or behave perfectly.
              Protect naps and meals, choose comfortable clothing and leave discipline and posing to the photographer
              during the session.
            </p>
          </div>
        </aside>
        <nav aria-label="In this guide" className="newborn-article-toc">
          <h2>In this guide</h2>
          <ol>
            <li>
              <a href="#plan">Kid-friendly plan</a>
            </li>
            <li>
              <a href="#tell">What to tell them</a>
            </li>
            <li>
              <a href="#day">Photo day rhythm</a>
            </li>
            <li>
              <a href="#bring">What to bring</a>
            </li>
            <li>
              <a href="#session">During the session</a>
            </li>
            <li>
              <a href="#hard-moment">Hard moments</a>
            </li>
          </ol>
        </nav>
        <section className="newborn-article-section" id="plan">
          <p className="newborn-article-kicker">LESS PRESSURE, MORE CONNECTION</p>
          <h2>Kid-Friendly Session Plan</h2>
          <div className="family-kids-plan">
            {kidPlan.map((item) => (
              <article key={item.n}>
                <span>{item.n}</span>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
        <section className="newborn-article-section newborn-article-split" id="tell">
          <div>
            <p className="newborn-article-kicker">BEFORE PHOTO DAY</p>
            <h2>What should you tell your children?</h2>
            <p>
              Say that you are meeting a photographer and spending time together. There may be walking, games, cuddles
              and a few moments of looking toward the camera.
            </p>
            <p>
              Avoid promises of perfect behavior, repeated smile practice or warnings about consequences. Those messages
              can make the session feel like a performance before it begins.
            </p>
          </div>
          <ArticlePhoto
            alt="Parents and baby smiling together on a park bench in autumn"
            className="newborn-article-split__photo"
            label="PHOTO: KEEP IT LIGHT"
            src={family5}
          />
        </section>
        <section className="newborn-article-section" id="day">
          <p className="newborn-article-kicker">A CALM ARRIVAL</p>
          <h2>Protect the photo-day rhythm</h2>
          <p>
            Keep naps and meals as normal as possible. Leave extra time for dressing and travel so one missing shoe does
            not turn into a rushed arrival. Avoid placing a full day of activities immediately before the session.
          </p>
        </section>
        <section className="newborn-article-section" id="bring">
          <p className="newborn-article-kicker">PACK LIGHT</p>
          <h2>What to bring for children</h2>
          <ul className="newborn-article-checklist">
            <li>
              <UiIcon name="check" />
              <span>Water and a clean, familiar snack.</span>
            </li>
            <li>
              <UiIcon name="check" />
              <span>Wipes and one backup outfit for young children.</span>
            </li>
            <li>
              <UiIcon name="check" />
              <span>One small comfort item or meaningful toy.</span>
            </li>
            <li>
              <UiIcon name="check" />
              <span>A practical layer for changing temperatures.</span>
            </li>
          </ul>
        </section>
        <section className="newborn-article-section newborn-article-split newborn-article-split--reverse" id="session">
          <ArticlePhoto
            alt="Mother with her children in a winter holiday studio portrait"
            className="newborn-article-split__photo"
            label="PHOTO: PLAY + CONNECTION"
            src={family6}
          />
          <div>
            <p className="newborn-article-kicker">LET ME LEAD</p>
            <h2>What parents should do during the session</h2>
            <p>
              Stay connected to your children and respond to the prompts I give you. You do not need to stand behind me,
              ask for smiles or correct every movement.
            </p>
            <p>
              Children often relax when parents stop monitoring the outcome. Movement, curiosity and affection are not
              interruptions—they are the material of the session.
            </p>
          </div>
        </section>
        <section className="newborn-article-section" id="hard-moment">
          <p className="newborn-article-kicker">WHEN SOMEONE NEEDS A RESET</p>
          <h2>What if a child has a hard moment?</h2>
          <p>
            We pause, reduce attention and let them reconnect with you. A snack, cuddle or quiet walk can change the
            mood. We can photograph another family member and return later. One difficult moment does not define the
            gallery.
          </p>
          <aside className="newborn-article-callout newborn-article-callout--tip">
            <span className="newborn-article-callout__icon">
              <UiIcon name="tip" />
            </span>
            <div>
              <h3>No perfect behavior required</h3>
              <p>
                Your children are allowed to be children. My job is to adapt the photography to the family in front of
                me.
              </p>
            </div>
          </aside>
        </section>
        <section className="newborn-article-cta family-article-cta">
          <div>
            <p className="newborn-article-kicker">A SESSION THEY CAN ENJOY</p>
            <h2>Tell me about your children</h2>
            <p>Explore the family package, or contact me with their ages and personalities for help planning the experience.</p>
          </div>
          <GuideCtaActions category="family" />
        </section>
      </div>
    </article>
  </main>
);
export default HowToPrepareKidsForFamilyPhotos;
